import type { Organization } from '@trmediaab/zebra-types/trhub';

import { extraInfoRequest as request } from './request';

interface Requests {
  init: Record<string, any>;
  complete: Record<string, any>;
}

declare global {
  interface Window {
    payex: any;
    trhubRequests: Requests;
  }
}

const noop = () => {};

export default class SwedbankPayClient {
  paymentMenu: null | { open: () => void; close: () => void } = null;

  orderId: string;
  organization: Organization;
  site: string;
  containerElementId: string;
  scriptElementId: string;
  verificationOnly: boolean;
  onBeforeOpen: () => void;
  onCompleted: (result: any) => void;
  onError: (error: any) => void;
  paymentUrl: string;
  requests: Requests;

  constructor({
    orderId,
    organization = 'trmedia_ab',
    site,
    containerElementId,
    scriptElementId,
    verificationOnly = false,
    onBeforeOpen = noop,
    onCompleted,
    onError,
  }: {
    orderId: string;
    organization?: Organization;
    site: string;
    containerElementId: string;
    scriptElementId: string;
    verificationOnly?: boolean;
    onBeforeOpen?: () => void;
    onCompleted: (result: any) => void;
    onError: (error: any) => void;
  }) {
    this.orderId = orderId;
    this.organization = organization;
    this.site = site;
    this.containerElementId = containerElementId;
    this.scriptElementId = scriptElementId;
    this.verificationOnly = verificationOnly;
    this.onBeforeOpen = onBeforeOpen;
    this.onCompleted = onCompleted;
    this.onError = onError;
    this.paymentUrl =
      window.self === window.top ? document.URL : document.referrer;

    // To avoid multiple calls to backend and possible race conditions,
    // we map all request promises in a global variable.
    // This happens when the checkout component is rendered multiple times
    // on the same page and the user returns from a 3-D secure verification.
    this.requests = window.trhubRequests = window.trhubRequests || {
      init: {},
      complete: {},
    };
  }

  destroy() {
    this.paymentMenu?.close();
    document.querySelector(`#${this.scriptElementId}`)?.remove();
  }

  createPaymentMenuOptions() {
    return {
      container: this.containerElementId,
      culture: 'sv-SE',
      onPaymentCompleted: async (paymentCompletedEvent: {
        paymentOrderId: string;
      }) => {
        try {
          const { paymentOrderId } = paymentCompletedEvent;

          if (!paymentOrderId) {
            throw new Error(
              'paymentOrderId is missing in paymentCompletedEvent',
            );
          }

          const { complete } = this.requests;

          if (!complete[paymentOrderId]) {
            // Complete the order
            complete[paymentOrderId] = request('swedbankpay/complete', {
              method: 'POST',
              body: {
                paymentOrderId,
                orderId: this.orderId,
                site: this.site,
              },
            });
          }

          const result = await complete[paymentOrderId];

          this.onCompleted(result);
        } catch (error) {
          this.onError(error);
        }
      },
      onPaymentFailed: async (event: unknown) => {
        this.onError(JSON.stringify(event));
      },
      onPaymentTransactionFailed: async (event: unknown) => {
        this.onError(JSON.stringify(event));
      },
      onExternalRedirect: (event: { redirectUrl: string }) => {
        if (window.parent) {
          window.parent.location.href = event.redirectUrl;
        } else {
          window.location.href = event.redirectUrl;
        }
      },
    };
  }

  /**
   * Open the Payment menu iframe after the paymentorder script is loaded or
   * payment page is revisited
   */
  openPaymentMenuIframe() {
    this.onBeforeOpen();
    const paymentMenuOptions = this.createPaymentMenuOptions();
    // initialize the Payment Menu inside our 'payment-menu' container.
    this.paymentMenu = window.payex.hostedView.paymentMenu(paymentMenuOptions);
    this.paymentMenu?.open();
  }

  /**
   * Appending the payment menu script
   */
  async init() {
    try {
      // Avoid inserting the same script multiple times
      if (document.querySelector(`#${this.scriptElementId}`)) {
        this.openPaymentMenuIframe();
        return;
      }

      const { init } = this.requests;

      if (!init[this.orderId]) {
        // Create the paymentorder
        init[this.orderId] = request('swedbankpay/paymentorder', {
          body: {
            orderId: this.orderId,
            verificationOnly: this.verificationOnly,
            paymentUrl: this.paymentUrl,
            organization: this.organization,
          },
          method: 'POST',
        });
      }

      const { data } = await init[this.orderId];

      if (!data?.href) {
        throw new Error('Inget skript returnerat från paymentorder');
      }

      // Create and append the script-tag
      const script = document.createElement('script');
      script.src = data.href;
      script.id = this.scriptElementId;

      script.addEventListener('load', () => {
        try {
          this.openPaymentMenuIframe();
        } catch (error) {
          this.onError(error);
        }
      });

      document.body.append(script);
    } catch (error) {
      return this.onError(error);
    }
  }
}
